import React, { type FunctionComponent, useMemo } from 'react'
import styled from '@emotion/styled'
import { InformationIcon } from '@instacart/ids-core'
import { Text, Tooltip } from '@retailer-platform/shared-components'
import {
  type PartnerPickDeviceDatum,
  type MarqetaCardsMetadata,
} from 'domains/partner-pick-devices/types/partnerPickDevices.types'
import { useDomainMessages, useDomainIntl } from 'domains/partner-pick-devices/utils/domain/intl'

const StatusContainer = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
})

const StyledList = styled.ul({
  listStyleType: 'disc',
  listStylePosition: 'inside',
})

const StyledText = styled(Text)({
  display: 'inline',
})

const cardTypeToCardMessage: Record<keyof MarqetaCardsMetadata, string> = {
  physical: 'partnerPickDevicesDomain.details.cardStatus.physical',
  digital: 'partnerPickDevicesDomain.details.cardStatus.digital',
  inactive: 'partnerPickDevicesDomain.details.cardStatus.inactive',
  terminated: 'partnerPickDevicesDomain.details.cardStatus.terminated',
  suspended: 'partnerPickDevicesDomain.details.cardStatus.suspended',
}

interface PartnerPickDevicesListCardStatusCellProps {
  partnerPickDevice: PartnerPickDeviceDatum
}

export const PartnerPickDevicesListCardStatusCell: FunctionComponent<
  PartnerPickDevicesListCardStatusCellProps
> = ({ partnerPickDevice: { marqetaCardsMetadata } }) => {
  const intl = useDomainIntl()
  const messages = useDomainMessages({
    multipleCards: 'partnerPickDevicesDomain.list.columns.cardStatus.multipleCards',
  })

  const [cardTypes, hasMultipleCards, singleCardType] = useMemo(() => {
    const cardTypes = Object.keys(marqetaCardsMetadata).filter(
      cardType => cardType !== '__typename'
    )
    const hasMultipleCards =
      cardTypes.reduce((acc, cardType) => acc + (marqetaCardsMetadata[cardType] ?? 0), 0) > 1
    const singleCardType = hasMultipleCards
      ? undefined
      : cardTypes.reduce(
          (acc, cardType) => (marqetaCardsMetadata[cardType] > 0 ? cardType : acc),
          undefined
        )

    return [
      cardTypes,
      cardTypes.reduce((acc, cardType) => acc + marqetaCardsMetadata[cardType] ?? 0, 0) > 1,
      singleCardType,
    ]
  }, [marqetaCardsMetadata])

  return hasMultipleCards ? (
    <Tooltip
      target={
        <StatusContainer>
          <Text>{messages.multipleCards}</Text>
          <InformationIcon size={16} color={'systemGrayscale30'} />
        </StatusContainer>
      }
      placement="bottom"
    >
      <StyledList>
        {cardTypes.map(
          cardType =>
            marqetaCardsMetadata[cardType] > 0 && (
              <li key={cardType}>
                <StyledText>{`${marqetaCardsMetadata[cardType]} ${intl.formatMessage({
                  id: cardTypeToCardMessage[cardType],
                })}`}</StyledText>
              </li>
            )
        )}
      </StyledList>
    </Tooltip>
  ) : (
    <Text>
      {singleCardType
        ? intl.formatMessage({ id: cardTypeToCardMessage[singleCardType], defaultMessage: '-' })
        : '-'}
    </Text>
  )
}
